import React, { useEffect, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { formatMoney } from 'utils/utils';
import endpoints from 'constants/endpoints';
import { safeAWSCallGet } from 'helpers/firebaseSetup';
import { parseDashboardForPayeeResponse } from 'helpers/dashboard/dashboardForPayee';
import Button from 'tailwind-components/Button';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { isCommissionsOverviewTabVisible } from 'helpers/viewing-rules';
import { isMobileWebView, postMobileNavigate } from 'helpers/mobileWebView';

const LINE_CHART_ACCENT = '#e4f082';
const MAX_CARRIER_COMMISSION_ROWS = 6;

function ChartCardLoader() {
  return (
    <div
      className='flex flex-col items-center justify-center gap-2 w-full min-h-[72px] flex-1'
      role='status'
      aria-live='polite'
      aria-label='Loading chart data'
    >
      <div
        className='h-6 w-6 rounded-full border-2 border-white/25 border-t-white animate-spin shrink-0'
        aria-hidden
      />
      <p className='text-[10px] font-medium text-white/55 m-0'>Loading...</p>
    </div>
  );
}

function ChartCardInlineMessage({ children, variant = 'muted' }) {
  const textClass = variant === 'error' ? 'text-white/90' : 'text-white/70';
  return (
    <div className='flex flex-1 min-h-[72px] w-full items-center justify-center px-2 py-2 text-center'>
      <p className={`m-0 text-[11px] font-medium leading-snug ${textClass}`}>
        {children}
      </p>
    </div>
  );
}

ChartCardInlineMessage.propTypes = {
  children: propTypes.node,
  variant: propTypes.oneOf(['muted', 'error'])
};

const GRID_STROKE = 'rgba(255,255,255,0.35)';
const AXIS_TICK = { fill: '#ffffff', fontSize: 9 };
const CHART_CARDS_HEIGHT_PX = 136;

function formatPeriodLabel(period) {
  if (!period || typeof period !== 'string') return '';
  const [y, m] = period.split('-');
  if (!y || !m) return period;
  return `${m}/${y.slice(2)}`;
}

function LineTooltip({ active, payload }) {
  if (active && payload?.length) {
    const row = payload[0]?.payload;
    if (row) {
      return (
        <div className='rounded-lg border border-white/25 bg-slate-900/90 px-3 py-2 text-sm text-white shadow-lg'>
          <div className='font-semibold'>{row.label}</div>
          <div className='text-white/85'>{formatMoney(row.value)}</div>
        </div>
      );
    }
  }
  return null;
}

LineTooltip.propTypes = {
  active: propTypes.bool,
  payload: propTypes.array
};

function CommissionTrendChartBody({
  metricsLoading,
  metricsError,
  lineData,
  lineChartYDomain
}) {
  if (metricsLoading) {
    return <ChartCardLoader />;
  }
  if (metricsError) {
    return (
      <ChartCardInlineMessage variant='error'>
        Unable to load commission trend.
      </ChartCardInlineMessage>
    );
  }
  if (lineData.length === 0) {
    return (
      <ChartCardInlineMessage>
        No trend data for this period.
      </ChartCardInlineMessage>
    );
  }
  return (
    <div className='relative min-h-0 w-full flex-1'>
      <div className='absolute inset-0 min-h-0'>
        <ResponsiveContainer width='100%' height='100%'>
          <LineChart
            data={lineData}
            margin={{ top: 4, right: 4, left: 2, bottom: 2 }}
          >
            <CartesianGrid
              strokeDasharray='3 3'
              stroke={GRID_STROKE}
              vertical={false}
            />
            <XAxis
              dataKey='label'
              tick={AXIS_TICK}
              interval='preserveStartEnd'
              axisLine={{ stroke: GRID_STROKE }}
              tickLine={{ stroke: GRID_STROKE }}
            />
            <YAxis
              tick={AXIS_TICK}
              width={32}
              axisLine={{ stroke: GRID_STROKE }}
              tickLine={{ stroke: GRID_STROKE }}
              domain={lineChartYDomain ?? ['auto', 'auto']}
              tickFormatter={v => (v >= 1000 ? `${v / 1000}k` : v)}
            />
            <Tooltip
              content={<LineTooltip />}
              cursor={{ stroke: GRID_STROKE }}
            />
            <Line
              type='linear'
              dataKey='value'
              stroke={LINE_CHART_ACCENT}
              strokeWidth={1.5}
              dot={{
                r: 3,
                fill: '#ffffff',
                stroke: LINE_CHART_ACCENT,
                strokeWidth: 1.5
              }}
              activeDot={{
                r: 4,
                fill: '#ffffff',
                stroke: LINE_CHART_ACCENT
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

CommissionTrendChartBody.propTypes = {
  metricsLoading: propTypes.bool.isRequired,
  metricsError: propTypes.bool.isRequired,
  lineData: propTypes.arrayOf(propTypes.object).isRequired,
  lineChartYDomain: propTypes.array
};

function segmentCommissionAmount(segment) {
  const raw =
    segment?.amount ?? segment?.total ?? segment?.value ?? segment?.count;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function CarrierMixBody({
  metricsLoading,
  metricsError,
  carrierCommissionRows
}) {
  if (metricsLoading) {
    return <ChartCardLoader />;
  }
  if (metricsError) {
    return (
      <ChartCardInlineMessage variant='error'>
        Unable to load carrier breakdown.
      </ChartCardInlineMessage>
    );
  }
  if (carrierCommissionRows.length === 0) {
    return (
      <div className='flex flex-1 min-h-0 items-center justify-center px-1 text-center'>
        <p className='m-0 text-[11px] font-medium leading-snug text-white/70'>
          No commission data found
        </p>
      </div>
    );
  }

  return (
    <ul
      className='m-0 min-h-0 list-none flex flex-1 flex-col gap-[3px] overflow-y-auto overflow-x-hidden p-0 [scrollbar-color:rgba(255,255,255,0.35)_transparent] [scrollbar-width:thin]'
      aria-label='Commission amounts by carrier'
    >
      {carrierCommissionRows.map((row, idx) => {
        const isLeader = idx === 0;
        const barWidth = Math.max(2, row.percent);
        return (
          <li
            key={row.key}
            className='group relative min-w-0 flex flex-col gap-[2px] text-[10px] leading-tight sm:text-[11px]'
          >
            <div className='flex min-w-0 items-baseline justify-between gap-2'>
              <span
                className={`min-w-0 truncate font-semibold ${
                  isLeader ? 'text-white' : 'text-white/90'
                }`}
                title={row.label}
              >
                {row.label}
              </span>
              <div className='shrink-0 flex items-baseline gap-1.5 tabular-nums'>
                <span className='text-white/75'>{formatMoney(row.amount)}</span>
                <span
                  className={`font-semibold ${
                    isLeader ? 'text-[#e4f082]' : 'text-white/95'
                  }`}
                >
                  {row.percent.toFixed(1)}%
                </span>
              </div>
            </div>
            <div
              className='h-[3px] w-full overflow-hidden rounded-full bg-white/15'
              aria-hidden
            >
              <div
                className='h-full rounded-full transition-[width] duration-500 ease-out'
                style={{
                  width: `${barWidth}%`,
                  backgroundColor: isLeader
                    ? LINE_CHART_ACCENT
                    : 'rgba(255,255,255,0.65)'
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
CarrierMixBody.propTypes = {
  metricsLoading: propTypes.bool.isRequired,
  metricsError: propTypes.bool.isRequired,
  carrierCommissionRows: propTypes.arrayOf(
    propTypes.shape({
      key: propTypes.string.isRequired,
      label: propTypes.string.isRequired,
      amount: propTypes.number.isRequired,
      percent: propTypes.number.isRequired
    })
  ).isRequired
};

export default function ProfileCharts({ user }) {
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const payeeZohoId =
      user?.id != null && String(user.id).trim() !== ''
        ? String(user.id).trim()
        : '';

    if (!payeeZohoId) {
      setMetrics(null);
      setMetricsError(false);
      setMetricsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setMetricsLoading(true);
    setMetricsError(false);
    setMetrics(null);

    (async () => {
      try {
        const response = await safeAWSCallGet(endpoints.aws.dashboardForPayee, {
          agent: payeeZohoId
        });
        if (cancelled) return;

        if (response?.status !== 200) {
          setMetricsError(true);
          setMetrics(null);
          return;
        }

        const parsed = parseDashboardForPayeeResponse(response.data);
        setMetrics(
          parsed ?? {
            commissionsTrend: null,
            carriersByCommissionPercentage: null
          }
        );
      } catch (err) {
        if (!cancelled) {
          console.error('ProfileCharts: dashboard-for-payee', err);
          setMetricsError(true);
          setMetrics(null);
        }
      } finally {
        if (!cancelled) {
          setMetricsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const trend = metrics?.commissionsTrend;
  const carrier = metrics?.carriersByCommissionPercentage;

  const lineData = useMemo(
    () =>
      (trend?.points ?? []).map(p => ({
        ...p,
        label: formatPeriodLabel(p.period)
      })),
    [trend?.points]
  );

  const lineChartYDomain = useMemo(() => {
    const values = lineData
      .map(d => Number(d.value))
      .filter(v => Number.isFinite(v));
    if (values.length === 0) return null;
    const low = Math.min(...values);
    const high = Math.max(...values);
    // snap so ticks sit on "nice" numbers (50s; large magnitudes use 100s).
    const step = Math.max(low, high) >= 2000 ? 100 : 50;
    let yMin = Math.floor(low / step) * step;
    let yMax = Math.ceil(high / step) * step;
    if (yMax <= yMin) yMax = yMin + step;
    return [yMin, yMax];
  }, [lineData]);

  const carrierCommissionRows = useMemo(() => {
    const segments = carrier?.segments ?? [];
    const perCarrier = segments
      .map(s => ({
        carrierId: s.carrierId,
        label:
          typeof s.label === 'string' && s.label.trim()
            ? s.label.trim()
            : 'Unknown carrier',
        amount: segmentCommissionAmount(s)
      }))
      .filter(r => r.amount > 0);
    const grandTotal = perCarrier.reduce((sum, r) => sum + r.amount, 0);
    if (grandTotal <= 0) return [];
    const sortedDesc = [...perCarrier].sort((a, b) => b.amount - a.amount);
    const capped = sortedDesc.slice(0, MAX_CARRIER_COMMISSION_ROWS);
    return capped.map(r => ({
      key: String(r.carrierId ?? r.label),
      label: r.label,
      amount: r.amount,
      percent: (r.amount / grandTotal) * 100
    }));
  }, [carrier?.segments]);

  return (
    <header
      data-tour='profile-charts'
      className='p-2 w-full flex flex-col gap-2 bg-primary-darkened rounded-lg text-white'
    >
      <section className='flex items-center justify-between'>
        <h2 className='m-0 leading-none text-lg'>Commission Insights</h2>
        {isCommissionsOverviewTabVisible(user) &&
          (isMobileWebView() ? (
            <Button
              data-tour='profile-charts-view-all'
              variant='primary'
              size='xsmall'
              text='View all commissions'
              icon={<ArrowRight />}
              iconPosition='right'
              onClick={() => postMobileNavigate('/commissions/overview')}
            />
          ) : (
            <Link
              data-tour='profile-charts-view-all'
              to='/commissions/overview'
            >
              <Button
                variant='primary'
                size='xsmall'
                text='View all commissions'
                icon={<ArrowRight />}
                iconPosition='right'
              />
            </Link>
          ))}
      </section>
      <div className='flex flex-col lg:flex-row items-stretch gap-2'>
        {/* MEMBER_COUNT_HIDDEN — temporarily hidden: the provider's member count is miscalculated. restore once fixed on the provider's side.
        <section
          data-tour='profile-charts-member-count'
          className='flex-none flex items-center lg:items-stretch'
        >
          <div className='rounded-lg bg-white/15 border border-white/20 backdrop-blur-sm px-3 py-2 flex flex-col justify-center gap-0.5 w-full lg:w-fit lg:min-w-[100px] text-center'>
            <p className='text-2xl font-bold text-white m-0 leading-tight tabular-nums'>
              {(user.Member_Count3 || 0).toLocaleString()}
            </p>
            <p className='text-xs font-semibold uppercase tracking-widest text-white/70 m-0 leading-none'>
              Member Count
            </p>
          </div>
        </section>
        */}
        <section
          data-tour='profile-charts-trend'
          className='rounded-lg bg-white/15 border border-white/20 backdrop-blur-sm flex-2 min-w-0 flex flex-col overflow-hidden h-auto'
          style={{
            minHeight: CHART_CARDS_HEIGHT_PX
          }}
        >
          <h3 className='m-0 px-2 pt-1.5 pb-0 text-base font-semibold uppercase tracking-wider text-white/75 shrink-0 leading-none'>
            Commission trend
          </h3>
          <div className='flex flex-1 min-h-0 min-w-0 flex-col px-1.5 pb-1.5 pr-2'>
            <CommissionTrendChartBody
              metricsLoading={metricsLoading}
              metricsError={metricsError}
              lineData={lineData}
              lineChartYDomain={lineChartYDomain}
            />
          </div>
        </section>
        <section
          data-tour='profile-charts-carrier-mix'
          className='rounded-lg bg-white/15 border border-white/20 backdrop-blur-sm flex-1 min-w-0 flex flex-col min-h-0 h-auto'
          style={{
            minHeight: CHART_CARDS_HEIGHT_PX
          }}
        >
          <h3 className='m-0 px-2 pt-1.5 pb-0 text-base font-semibold uppercase tracking-wider text-white/75 shrink-0 leading-none'>
            Carrier mix
          </h3>
          <div className='flex flex-1 min-h-0 w-full flex-col px-2 pb-1.5 pt-0'>
            <CarrierMixBody
              metricsLoading={metricsLoading}
              metricsError={metricsError}
              carrierCommissionRows={carrierCommissionRows}
            />
          </div>
        </section>
      </div>
    </header>
  );
}

ProfileCharts.propTypes = {
  user: propTypes.object.isRequired
};
